import { useRef, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentGame, updateGameState, fetchGameState } from '@/store/gameSlice';
import { setInteractionEnabled, setShowLandingPad, selectAnimationPhase, setAnimationPhase } from '@/store/uiSlice';
import { selectUser } from '@/store/authSlice';
import { apiClient } from '@/services/api';
import { hexToPixel } from '@/utils/hexGrid';
import { level1BoardSpan, surveyBoardZoom, surveyCanvasSize, surveyMoonRadius } from '@/utils/fitBoardZoom';
import type { AppDispatch } from '@/store';
import './StartGameAnimation.css';

const DESIGN_MOON_RADIUS = 150;

function surveyFrame(canvas: HTMLCanvasElement) {
  const moonRadius = surveyMoonRadius(canvas.width, canvas.height);
  return {
    moonX: canvas.width / 2,
    moonY: canvas.height / 2,
    moonRadius,
    layout: moonRadius / DESIGN_MOON_RADIUS,
  };
}

function paintMoon(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement | null,
  moonX: number,
  moonY: number,
  radius: number,
) {
  if (image) {
    ctx.drawImage(image, moonX - radius, moonY - radius, radius * 2, radius * 2);
    return;
  }
  ctx.fillStyle = '#888888';
  ctx.beginPath();
  ctx.arc(moonX, moonY, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#aaaaaa';
  ctx.lineWidth = 2;
  ctx.stroke();
}

/**
 * StartGameAnimation - Displays the Start Game phase animation
 * 
 * Animation Sequence:
 * Part 1: Moon Approach (Space View) - Top-down perspective throughout
 * - Initial approach: Spaceship from above, moon grows larger
 * - The lander stays in front of the moon, orbiting downward and shrinking
 * - The moon grows, then fades into the land survey
 * 
 * Part 2: Map Landing (Game Board View)
 * - Cut to map view
 * - Entry from random side
 * - Map flight
 * - Landing on base tile
 * - Transformation to base
 */
export default function StartGameAnimation() {
  const game = useSelector(selectCurrentGame);
  const user = useSelector(selectUser);
  const animationPhase = useSelector(selectAnimationPhase);
  const dispatch = useDispatch<AppDispatch>();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();
  const [spritesLoaded, setSpritesLoaded] = useState(false);
  const [spriteImages, setSpriteImages] = useState<{ [key: string]: HTMLImageElement }>({});
  const [animationProgress, setAnimationProgress] = useState(0); // 0 to 1
  const [overlayOpacity, setOverlayOpacity] = useState(1.0); // For fading out overlay to reveal game screen
  const [revealBoard, setRevealBoard] = useState(false);
  const revealBoardRef = useRef(false);
  const entrySideRef = useRef<'top' | 'bottom' | 'left' | 'right' | null>(null); // Random entry side for map entry
  const phaseCompleteCallledRef = useRef(false); // Track if we've called the phase_complete API
  
  // Animation state
  const moonScaleRef = useRef(0.3); // Start small (30% of target size)
  const spaceshipScaleRef = useRef(1.0); // Start at full size
  const spaceshipXRef = useRef(0);
  const spaceshipYRef = useRef(0);
  const startTimeRef = useRef<number | null>(null);
  const moonImageRef = useRef<HTMLImageElement | null>(null);
  const shipAngleRef = useRef(0); // Store previous angle for smooth rotation
  
  // Smooth easing function - cubic ease out for gentle deceleration
  const easeOutCubic = useCallback((t: number): number => {
    return 1 - Math.pow(1 - t, 3);
  }, []);
  
  // Ease in-out cubic - smooth acceleration and deceleration with velocity continuity
  const easeInOutCubic = useCallback((t: number): number => {
    return t < 0.5 
      ? 4 * t * t * t 
      : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }, []);

  // Report phase completion to server (for multiplayer synchronization)
  const reportPhaseComplete = useCallback(async (gameId: string, _playerId: string, etag?: string) => {
    try {
      console.log('🎬 Reporting start_game phase completion to server...');
      const data = await apiClient.phaseComplete({
        gameId,
        phase: 'start_game',
        etag,
      });
      console.log('✅ Phase completion reported:', data);
      if (data.gameState) {
        console.log(`🔄 Updating game state immediately from phase_complete response`);
        dispatch(updateGameState(data.gameState));
      }
      if (data.allPlayersReady) {
        console.log(`🎉 All players ready! Transitioning to ${data.newPhase}`);
        if (!data.gameState && gameId) {
          dispatch(fetchGameState(gameId));
        }
      } else {
        console.log(`⏳ Waiting for other players: ${data.completedPlayers?.length}/${data.totalPlayers}`);
      }
    } catch (error) {
      console.error('❌ Error reporting phase completion:', error);
      console.error('❌ Lambda function may not be available.');
      console.error('❌ Use debug mode arrows to manually advance to land_claim phase.');
    }
  }, [dispatch]);

  // Load sprites on mount
  useEffect(() => {
    console.log('🚀 StartGameAnimation: Component mounted, loading sprites...');
    const loadSprites = async () => {
      const sprites = [
        { name: 'spaceship-rear-8bit.png', folder: 'sprites' },
        { name: 'spaceship-side-8bit.png', folder: 'sprites' },
        { name: 'spaceship-front-8bit.png', folder: 'sprites' },
        { name: 'spaceship-top-8bit.png', folder: 'sprites' },
        { name: 'flame-rear-8bit.png', folder: 'sprites' },
        { name: 'flame-side-8bit.png', folder: 'sprites' },
        { name: 'flame-front-8bit.png', folder: 'sprites' },
        { name: 'flame-top-8bit.png', folder: 'sprites' },
        { name: 'base-8bit.png', folder: 'icons' }, // base is in icons folder
      ];

      const loadedImages: { [key: string]: HTMLImageElement } = {};

      // Load all sprites from get_icon API (S3 via Lambda)
      const loadPromises = sprites.map(async (sprite) => {
        try {
          console.log(`📦 Loading sprite from API: ${sprite.name}`);
          
          // Load sprite from get_icon API
          const { apiClient } = await import('@/services/api');
          const dataUrl = await apiClient.getIcon(sprite.name);
          
          // Preload image from data URL
          return new Promise<{ name: string; img: HTMLImageElement }>((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
              console.log(`✅ Loaded sprite from API: ${sprite.name}`);
              resolve({ name: sprite.name, img });
            };
            img.onerror = (e) => {
              console.error(`❌ Failed to load sprite: ${sprite.name}`, e);
              reject(e);
            };
            img.src = dataUrl; // Use data URL from API
          });
        } catch (error) {
          console.error(`❌ Error loading sprite ${sprite.name}:`, error);
          return null;
        }
      });

      const imageResults = await Promise.all(loadPromises);
      imageResults.forEach((result) => {
        if (result) {
          loadedImages[result.name] = result.img;
        }
      });

      setSpriteImages(loadedImages);
      const loaded = Object.keys(loadedImages).length === sprites.length;
      setSpritesLoaded(loaded);
      console.log(`🎨 Sprites loading complete: ${loaded ? 'SUCCESS' : 'FAILED'} (${Object.keys(loadedImages).length}/${sprites.length})`);
    };

    loadSprites();
  }, []);

  // Load moon image (simplified moon representation)
  useEffect(() => {
    // Always create moon image, even without landmark
    if (game?.phase === 'start_game') {
      // Create a simple moon image representation
      const moonCanvas = document.createElement('canvas');
      moonCanvas.width = 300;
      moonCanvas.height = 300;
      const moonCtx = moonCanvas.getContext('2d');
      
      if (moonCtx) {
        // Draw moon base (gray circle)
        moonCtx.fillStyle = '#888888';
        moonCtx.beginPath();
        moonCtx.arc(150, 150, 150, 0, Math.PI * 2);
        moonCtx.fill();
        
        // Draw craters (more detail for larger moon)
        moonCtx.fillStyle = '#666666';
        moonCtx.beginPath();
        moonCtx.arc(100, 120, 20, 0, Math.PI * 2);
        moonCtx.fill();
        moonCtx.beginPath();
        moonCtx.arc(210, 195, 30, 0, Math.PI * 2);
        moonCtx.fill();
        moonCtx.beginPath();
        moonCtx.arc(150, 75, 18, 0, Math.PI * 2);
        moonCtx.fill();
        moonCtx.beginPath();
        moonCtx.arc(80, 200, 15, 0, Math.PI * 2);
        moonCtx.fill();
        moonCtx.beginPath();
        moonCtx.arc(220, 100, 22, 0, Math.PI * 2);
        moonCtx.fill();
        
        // Convert to image
        const img = new Image();
        img.src = moonCanvas.toDataURL();
        moonImageRef.current = img;
      }
    }
  }, [game?.phase]);

  // Draw stars background (randomly placed)
  const drawStars = useCallback((ctx: CanvasRenderingContext2D, width: number, height: number) => {
    ctx.fillStyle = '#ffffff';
    // Draw random stars (using seeded random for consistency across frames)
    const starCount = 150;
    // Use a simple seeded random function for consistent star placement
    let seed = 12345; // Fixed seed for consistent star pattern
    const random = () => {
      seed = (seed * 9301 + 49297) % 233280;
      return seed / 233280;
    };
    
    for (let i = 0; i < starCount; i++) {
      const x = random() * width;
      const y = random() * height;
      const size = Math.floor(random() * 3) + 1; // Random size 1-3 pixels
      ctx.fillRect(x, y, size, size);
    }
  }, []);
  
  // Helper function to smoothly interpolate angles (handles wrap-around)
  const lerpAngle = useCallback((from: number, to: number, t: number): number => {
    // Normalize angles to 0-2π range
    from = from % (Math.PI * 2);
    to = to % (Math.PI * 2);
    
    // Find shortest path
    let diff = to - from;
    if (diff > Math.PI) {
      diff -= Math.PI * 2;
    } else if (diff < -Math.PI) {
      diff += Math.PI * 2;
    }
    
    return from + diff * t;
  }, []);

  // Helper function to draw spaceship consistently (top-down view with visible flames)
  const drawSpaceship = useCallback((
    ctx: CanvasRenderingContext2D,
    centerX: number,
    centerY: number,
    scale: number,
    angle: number,
    opacity: number,
    timestamp: number,
    spaceshipImg: HTMLImageElement,
    flameImg: HTMLImageElement | null
  ) => {
    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.translate(centerX, centerY);
    ctx.rotate(angle);
    
    // Draw flame first (behind spaceship, animated)
    // For top-down view, flames are positioned directly behind the ship
    if (flameImg) {
      const flameScale = scale * (1.0 + Math.sin(timestamp / 100) * 0.1);
      ctx.save();
      // Don't rotate flame - it's already oriented correctly for top-down
      // Offset flames slightly behind the ship
      ctx.translate(0, 20 * scale); // Position flames behind ship in top-down view
      ctx.scale(flameScale, flameScale);
      ctx.drawImage(flameImg, -16, -16, 32, 32);
      ctx.restore();
    }
    
    // Draw spaceship on top of flames
    ctx.save();
    ctx.scale(scale, scale);
    ctx.drawImage(spaceshipImg, -32, -32, 64, 64);
    ctx.restore();
    
    ctx.restore();
  }, []);

  // Animation loop for moon approach phase
  const animateMoonApproach = useCallback((timestamp: number) => {
    if (!canvasRef.current) {
      console.warn('⚠️  Canvas ref not available');
      return;
    }
    if (!spritesLoaded) {
      console.warn('⚠️  Sprites not loaded yet');
      return;
    }
    // Use default landmark if game landmark is not available
    const landmark = game?.landmark || {
      displayName: 'Lunar Surface',
      latitude: 0,
      longitude: 0,
      type: 'crater'
    };
    
    if (!game?.landmark) {
      console.warn('⚠️  Game landmark not available, using default');
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Initialize start time
    if (startTimeRef.current === null) {
      startTimeRef.current = timestamp;
    }

    const elapsed = timestamp - startTimeRef.current;
    const duration = 3000; // 3 seconds for initial approach
    const rawProgress = Math.min(elapsed / duration, 1);
    
    // Use easeOutCubic for gentle, continuous deceleration toward end
    const progress = easeOutCubic(rawProgress);

    // Clear canvas
    ctx.fillStyle = '#000011'; // Deep space background
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw stars
    drawStars(ctx, canvas.width, canvas.height);

    // Moon grows from 0.3 to 1.0. Full size matches the fitted land-claim board.
    moonScaleRef.current = 0.3 + (progress * 0.7);
    const { moonX, moonY, moonRadius, layout } = surveyFrame(canvas);
    paintMoon(ctx, moonImageRef.current, moonX, moonY, moonRadius * moonScaleRef.current);

    // Draw spaceship - top-down view throughout
    const spaceshipImg = spriteImages['spaceship-top-8bit.png'];
    const flameImg = spriteImages['flame-top-8bit.png'];
    
    if (spaceshipImg) {
      const shipCenterX = canvas.width / 2;
      const shipCenterY = Math.min(canvas.height - 48 * layout, moonY + moonRadius + 160 * layout);
      
      drawSpaceship(
        ctx,
        shipCenterX,
        shipCenterY,
        layout,
        0, // No rotation (pointing up)
        1.0, // Full opacity
        timestamp,
        spaceshipImg,
        flameImg
      );
    }

    // Update progress
    setAnimationProgress(rawProgress);

    // Continue animation if not complete
    if (rawProgress < 1) {
      animationFrameRef.current = requestAnimationFrame(animateMoonApproach);
    } else {
      // Phase 1 complete, move to camera lock phase
      dispatch(setAnimationPhase('camera_lock'));
      startTimeRef.current = null; // Reset for next phase
      moonScaleRef.current = 1.0; // Moon is now at full size
      spaceshipScaleRef.current = 1.0; // Reset spaceship scale for next phase
      shipAngleRef.current = 0; // Reset angle tracking
    }
  }, [spritesLoaded, spriteImages, easeOutCubic, drawStars, drawSpaceship]);

  // Animation loop for camera lock phase
  const animateCameraLock = useCallback((timestamp: number) => {
    if (!canvasRef.current || !spritesLoaded) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Initialize start time
    if (startTimeRef.current === null) {
      startTimeRef.current = timestamp;
    }

    const elapsed = timestamp - startTimeRef.current;
    const duration = 7000;
    const rawProgress = Math.min(elapsed / duration, 1);
    const progress = rawProgress;

    const { moonX, moonY, moonRadius, layout } = surveyFrame(canvas);
    const moonScale = 1 + easeInOutCubic(Math.min(progress / 0.85, 1)) * 0.7;
    moonScaleRef.current = moonScale;
    const reveal = Math.max(0, (progress - 0.62) / 0.38);
    if (reveal > 0 && !revealBoardRef.current) {
      revealBoardRef.current = true;
      setRevealBoard(true);
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.globalAlpha = 1 - reveal;
    ctx.fillStyle = '#000011';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    drawStars(ctx, canvas.width, canvas.height);
    paintMoon(ctx, moonImageRef.current, moonX, moonY, moonRadius * moonScale);
    ctx.restore();

    // One spiral from the approach point down onto the moon. The nose follows
    // the tangent, so the turn is continuous instead of a corner.
    const spiralT = Math.min(progress / 0.95, 1);
    const approachX = canvas.width / 2;
    const approachY = Math.min(canvas.height - 48 * layout, moonY + moonRadius + 160 * layout);
    const startAngle = Math.atan2(approachY - moonY, approachX - moonX);
    const startRadius = Math.hypot(approachX - moonX, approachY - moonY);
    const endRadius = Math.max(16 * layout, moonRadius * 0.18);
    const turns = 1.35;

    const spiralAt = (t: number) => {
      const clamped = Math.max(0, Math.min(1, t));
      const radiusT = clamped * clamped * (3 - 2 * clamped);
      const radius = startRadius + (endRadius - startRadius) * radiusT;
      const theta = startAngle + turns * Math.PI * 2 * clamped;
      return {
        x: moonX + Math.cos(theta) * radius,
        y: moonY + Math.sin(theta) * radius,
      };
    };

    const here = spiralAt(spiralT);
    const ahead = spiralAt(Math.min(1, spiralT + 0.015));
    const shipCenterX = here.x;
    const shipCenterY = here.y;
    const shipScale = 0.9 - spiralT * 0.55;
    const shipOpacity = 1;
    const targetAngle = Math.atan2(ahead.y - here.y, ahead.x - here.x) + Math.PI / 2;

    // Nose follows the spiral. A small step keeps the turn from snapping.
    const shipAngle = lerpAngle(shipAngleRef.current, targetAngle, 0.045);
    shipAngleRef.current = shipAngle;
    
    // Use top-down sprite throughout
    const spaceshipImg = spriteImages['spaceship-top-8bit.png'];
    const flameImg = spriteImages['flame-top-8bit.png'];
    
    if (spaceshipImg) {
      drawSpaceship(
        ctx,
        shipCenterX,
        shipCenterY,
        shipScale * layout,
        shipAngle,
        shipOpacity,
        timestamp,
        spaceshipImg,
        flameImg
      );
    }

    // Update progress
    setAnimationProgress(rawProgress);

    // Continue animation if not complete
    if (rawProgress < 1) {
      animationFrameRef.current = requestAnimationFrame(animateCameraLock);
    } else {
      const landed = spiralAt(1);
      spaceshipXRef.current = landed.x;
      spaceshipYRef.current = landed.y;
      spaceshipScaleRef.current = 0.35 * layout;
      dispatch(setAnimationPhase('map_flight'));
      startTimeRef.current = null;
    }
  }, [spritesLoaded, spriteImages, drawStars, drawSpaceship, lerpAngle]);

  // Animation loop for orbital flight phase
  const animateOrbitalFlight = useCallback((timestamp: number) => {
    if (!canvasRef.current || !spritesLoaded) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Initialize start time
    if (startTimeRef.current === null) {
      startTimeRef.current = timestamp;
    }

    const elapsed = timestamp - startTimeRef.current;
    const duration = 3500; // 3.5 seconds for orbital flight
    const rawProgress = Math.min(elapsed / duration, 1);
    const progress = easeInOutCubic(rawProgress);

    // Clear canvas
    ctx.fillStyle = '#000011';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw stars
    drawStars(ctx, canvas.width, canvas.height);

    const { moonX, moonY, moonRadius, layout } = surveyFrame(canvas);
    paintMoon(ctx, moonImageRef.current, moonX, moonY, moonRadius);

    // Spaceship orbits around moon - use top view
    const spaceshipImg = spriteImages['spaceship-top-8bit.png'];
    const flameImg = spriteImages['flame-top-8bit.png'];
    
    if (spaceshipImg) {
      const angle = progress * Math.PI * 2;
      const orbitRadiusX = 250 * layout;
      const orbitRadiusY = 180 * layout;
      
      const shipX = moonX + Math.cos(angle) * orbitRadiusX;
      const shipY = moonY + Math.sin(angle) * orbitRadiusY;
      const shipScale = (0.4 + (Math.sin(angle) * 0.1)) * layout;
      
      const distanceFromMoon = Math.sqrt(
        Math.pow(shipX - moonX, 2) + Math.pow(shipY - moonY, 2)
      );
      const isBehindMoon = distanceFromMoon < moonRadius * 1.2 && 
                          Math.abs(shipX - moonX) < moonRadius * 0.8;
      
      if (!isBehindMoon || distanceFromMoon < moonRadius * 0.5) {
        const opacity = isBehindMoon ? 0.3 : 1.0;
        
        drawSpaceship(
          ctx,
          shipX,
          shipY,
          shipScale,
          angle + Math.PI / 2,
          opacity,
          timestamp,
          spaceshipImg,
          flameImg
        );
      }
    }

    setAnimationProgress(rawProgress);

    if (rawProgress < 1) {
      animationFrameRef.current = requestAnimationFrame(animateOrbitalFlight);
    } else {
      dispatch(setAnimationPhase('map_landing'));
      startTimeRef.current = null;
    }
  }, [spritesLoaded, spriteImages, easeInOutCubic, drawStars, drawSpaceship]);

  // Animation loop for map cut transition (Issue #51: Cut to map view)
  // Fades out the overlay to reveal the game screen underneath
  const animateMapCut = useCallback((timestamp: number) => {
    // Initialize start time
    if (startTimeRef.current === null) {
      startTimeRef.current = timestamp;
    }

    const elapsed = timestamp - startTimeRef.current;
    const duration = 800; // 0.8 seconds for smooth fade out
    const rawProgress = Math.min(elapsed / duration, 1);
    
    // Fade out overlay to reveal game screen underneath
    const fadeOutProgress = easeInOutCubic(rawProgress);
    const opacity = 1.0 - fadeOutProgress; // Fade from 1.0 to 0.0
    setOverlayOpacity(opacity);

    // Update progress
    setAnimationProgress(rawProgress);

    // Continue animation if not complete
    if (rawProgress < 1) {
      animationFrameRef.current = requestAnimationFrame(animateMapCut);
    } else {
      // Map cut complete, make overlay visible again (but transparent background for game board)
      // Move to map entry phase (spaceship will fly in from random side)
      dispatch(setAnimationPhase('map_entry'));
      startTimeRef.current = null;
      setOverlayOpacity(1.0); // Make overlay visible again for spaceship animation
      // Select random entry side
      const sides: Array<'top' | 'bottom' | 'left' | 'right'> = ['top', 'bottom', 'left', 'right'];
      entrySideRef.current = sides[Math.floor(Math.random() * sides.length)];
    }
  }, [easeInOutCubic]);

  // Animation loop for map entry (Issue #52: Spaceship entry to map)
  const animateMapEntry = useCallback((timestamp: number) => {
    if (!canvasRef.current || !spritesLoaded || !entrySideRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Initialize start time
    if (startTimeRef.current === null) {
      startTimeRef.current = timestamp;
    }

    const elapsed = timestamp - startTimeRef.current;
    const duration = 1000; // 1 second for entry flight
    const rawProgress = Math.min(elapsed / duration, 1);
    const progress = easeInOutCubic(rawProgress);

    const zoom = surveyBoardZoom(canvas.width, canvas.height);
    const span = level1BoardSpan();
    const gameBoardWidth = span.width * zoom;
    const gameBoardHeight = span.height * zoom;
    const gameBoardCenterX = canvas.width / 2;
    const gameBoardCenterY = canvas.height / 2;
    
    // Calculate entry and target positions based on random side
    let startX: number, startY: number, targetX: number, targetY: number;
    
    const entrySide = entrySideRef.current;
    const offset = 100 * zoom;
    
    switch (entrySide) {
      case 'top':
        startX = gameBoardCenterX;
        startY = gameBoardCenterY - gameBoardHeight / 2 - offset;
        targetX = gameBoardCenterX;
        targetY = gameBoardCenterY;
        break;
      case 'bottom':
        startX = gameBoardCenterX;
        startY = gameBoardCenterY + gameBoardHeight / 2 + offset;
        targetX = gameBoardCenterX;
        targetY = gameBoardCenterY;
        break;
      case 'left':
        startX = gameBoardCenterX - gameBoardWidth / 2 - offset;
        startY = gameBoardCenterY;
        targetX = gameBoardCenterX;
        targetY = gameBoardCenterY;
        break;
      case 'right':
        startX = gameBoardCenterX + gameBoardWidth / 2 + offset;
        startY = gameBoardCenterY;
        targetX = gameBoardCenterX;
        targetY = gameBoardCenterY;
        break;
    }
    
    // Interpolate position
    const currentX = startX + (targetX - startX) * progress;
    const currentY = startY + (targetY - startY) * progress;
    
    // Calculate angle toward target (smooth rotation)
    const dx = targetX - currentX;
    const dy = targetY - currentY;
    const targetAngle = Math.atan2(dy, dx) + Math.PI / 2; // Add 90° for sprite orientation
    const shipAngle = lerpAngle(shipAngleRef.current, targetAngle, 0.15);
    shipAngleRef.current = shipAngle;
    
    // Clear canvas with transparent background (game board shows through)
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw spaceship (use top-down view for map)
    const spaceshipImg = spriteImages['spaceship-top-8bit.png'];
    const flameImg = spriteImages['flame-top-8bit.png'];
    
    if (spaceshipImg) {
      const shipScale = 0.85 * zoom; // Same scale as the fitted board
      drawSpaceship(
        ctx,
        currentX,
        currentY,
        shipScale,
        shipAngle,
        1.0, // Full opacity
        timestamp,
        spaceshipImg,
        flameImg
      );
    }

    // Update progress
    setAnimationProgress(rawProgress);

    // Continue animation if not complete
    if (rawProgress < 1) {
      animationFrameRef.current = requestAnimationFrame(animateMapEntry);
    } else {
      // Map entry complete, move to map flight phase
      dispatch(setAnimationPhase('map_flight'));
      startTimeRef.current = null;
      // Keep current position for continuous animation
      spaceshipXRef.current = currentX;
      spaceshipYRef.current = currentY;
    }
  }, [spritesLoaded, spriteImages, easeInOutCubic, drawSpaceship, lerpAngle]);

  // Animation loop for map flight (Issue #53: Spaceship map flight - Scouting behavior)
  const animateMapFlight = useCallback((timestamp: number) => {
    if (!canvasRef.current || !spritesLoaded) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Initialize start time
    if (startTimeRef.current === null) {
      startTimeRef.current = timestamp;
    }

    const elapsed = timestamp - startTimeRef.current;
    const duration = 8000; // 8 seconds - comfortable pace without being too long
    const rawProgress = Math.min(elapsed / duration, 1);
    const progress = easeInOutCubic(rawProgress);

    const zoom = surveyBoardZoom(canvas.width, canvas.height);
    const span = level1BoardSpan();
    const gameBoardWidth = span.width * zoom;
    const gameBoardHeight = span.height * zoom;
    const gameBoardCenterX = canvas.width / 2;
    const gameBoardCenterY = canvas.height / 2;

    // Start from where map_entry left off (center of board)
    const startX = spaceshipXRef.current;
    const startY = spaceshipYRef.current;

    // Define MUCH LARGER scouting path covering the entire board
    // Path: Center -> Right-Top -> Right-Bottom -> Left-Bottom -> Left-Top -> Above landing pad
    // This creates a comprehensive reconnaissance covering all quadrants
    
    // Waypoints for FULL BOARD scouting - covering all areas
    const waypoint1X = gameBoardCenterX + gameBoardWidth * 0.38; // Far right
    const waypoint1Y = gameBoardCenterY - gameBoardHeight * 0.35; // Upper right quadrant

    const waypoint2X = gameBoardCenterX + gameBoardWidth * 0.38; // Far right
    const waypoint2Y = gameBoardCenterY + gameBoardHeight * 0.35; // Lower right quadrant

    const waypoint3X = gameBoardCenterX - gameBoardWidth * 0.38; // Far left
    const waypoint3Y = gameBoardCenterY + gameBoardHeight * 0.35; // Lower left quadrant

    const waypoint4X = gameBoardCenterX - gameBoardWidth * 0.38; // Far left
    const waypoint4Y = gameBoardCenterY - gameBoardHeight * 0.35; // Upper left quadrant

    // Final position: above landing pad (center hex) - MUST be exact center
    const baseHex = { q: 0, r: 0 };
    const basePixel = hexToPixel(baseHex, gameBoardCenterX, gameBoardCenterY);
    const finalX = basePixel.x;
    const finalY = basePixel.y - 100 * zoom; // Position well above landing pad for smooth approach

    // Multi-segment path with VERY WIDE arcs using cubic bezier curves
    let currentX: number, currentY: number;

    if (progress < 0.20) {
      // Segment 1: Start -> Waypoint 1 (Upper Right) - WIDE ARC
      const segmentProgress = progress / 0.20;
      const easedProgress = easeInOutCubic(segmentProgress);

      // Very wide cubic bezier arc
      const control1X = startX + (waypoint1X - startX) * 0.2;
      const control1Y = startY - 100; // Large upward arc
      const control2X = startX + (waypoint1X - startX) * 0.8;
      const control2Y = waypoint1Y - 80; // Continue wide arc

      const t = easedProgress;
      const mt = 1 - t;
      currentX = mt*mt*mt*startX + 3*mt*mt*t*control1X + 3*mt*t*t*control2X + t*t*t*waypoint1X;
      currentY = mt*mt*mt*startY + 3*mt*mt*t*control1Y + 3*mt*t*t*control2Y + t*t*t*waypoint1Y;

    } else if (progress < 0.40) {
      // Segment 2: Waypoint 1 -> Waypoint 2 (Right edge sweep down) - GENTLE CURVE
      const segmentProgress = (progress - 0.20) / 0.20;
      const easedProgress = easeInOutCubic(segmentProgress);

      // Gentle curve along right edge
      const control1X = waypoint1X + 60; // Arc outward
      const control1Y = waypoint1Y + (waypoint2Y - waypoint1Y) * 0.3;
      const control2X = waypoint2X + 60; // Continue arc
      const control2Y = waypoint1Y + (waypoint2Y - waypoint1Y) * 0.7;

      const t = easedProgress;
      const mt = 1 - t;
      currentX = mt*mt*mt*waypoint1X + 3*mt*mt*t*control1X + 3*mt*t*t*control2X + t*t*t*waypoint2X;
      currentY = mt*mt*mt*waypoint1Y + 3*mt*mt*t*control1Y + 3*mt*t*t*control2Y + t*t*t*waypoint2Y;

    } else if (progress < 0.60) {
      // Segment 3: Waypoint 2 -> Waypoint 3 (Sweep across bottom) - VERY WIDE ARC
      const segmentProgress = (progress - 0.40) / 0.20;
      const easedProgress = easeInOutCubic(segmentProgress);

      // Wide sweeping arc across bottom of board
      const control1X = waypoint2X - (waypoint2X - waypoint3X) * 0.25;
      const control1Y = waypoint2Y + 80; // Arc downward (outward from board)
      const control2X = waypoint2X - (waypoint2X - waypoint3X) * 0.75;
      const control2Y = waypoint3Y + 80; // Continue wide arc

      const t = easedProgress;
      const mt = 1 - t;
      currentX = mt*mt*mt*waypoint2X + 3*mt*mt*t*control1X + 3*mt*t*t*control2X + t*t*t*waypoint3X;
      currentY = mt*mt*mt*waypoint2Y + 3*mt*mt*t*control1Y + 3*mt*t*t*control2Y + t*t*t*waypoint3Y;

    } else if (progress < 0.80) {
      // Segment 4: Waypoint 3 -> Waypoint 4 (Left edge sweep up) - GENTLE CURVE
      const segmentProgress = (progress - 0.60) / 0.20;
      const easedProgress = easeInOutCubic(segmentProgress);

      // Gentle curve along left edge
      const control1X = waypoint3X - 60; // Arc outward
      const control1Y = waypoint3Y - (waypoint3Y - waypoint4Y) * 0.3;
      const control2X = waypoint4X - 60; // Continue arc
      const control2Y = waypoint3Y - (waypoint3Y - waypoint4Y) * 0.7;

      const t = easedProgress;
      const mt = 1 - t;
      currentX = mt*mt*mt*waypoint3X + 3*mt*mt*t*control1X + 3*mt*t*t*control2X + t*t*t*waypoint4X;
      currentY = mt*mt*mt*waypoint3Y + 3*mt*mt*t*control1Y + 3*mt*t*t*control2Y + t*t*t*waypoint4Y;

    } else {
      // Segment 5: Waypoint 4 -> Above landing pad (Final approach) - SMOOTH CURVE TO CENTER
      const segmentProgress = (progress - 0.80) / 0.20;
      const easedProgress = easeInOutCubic(segmentProgress);

      // Smooth converging curve to center landing pad
      const control1X = waypoint4X + (finalX - waypoint4X) * 0.25;
      const control1Y = waypoint4Y - 100; // Arc upward
      const control2X = waypoint4X + (finalX - waypoint4X) * 0.75;
      const control2Y = finalY - 50; // Approach from above

      const t = easedProgress;
      const mt = 1 - t;
      currentX = mt*mt*mt*waypoint4X + 3*mt*mt*t*control1X + 3*mt*t*t*control2X + t*t*t*finalX;
      currentY = mt*mt*mt*waypoint4Y + 3*mt*mt*t*control1Y + 3*mt*t*t*control2Y + t*t*t*finalY;
    }

    // Calculate smooth direction angle - look AHEAD with small delta
    const dt = 0.01; // Very small delta for smooth angle updates
    const nextProgress = Math.min(1, rawProgress + dt);

    // Calculate next position for smooth angle (replicate path calculation)
    let nextX: number, nextY: number;
    
    if (nextProgress < 0.20) {
      const segmentProgress = nextProgress / 0.20;
      const easedProgress = easeInOutCubic(segmentProgress);
      const control1X = startX + (waypoint1X - startX) * 0.2;
      const control1Y = startY - 100;
      const control2X = startX + (waypoint1X - startX) * 0.8;
      const control2Y = waypoint1Y - 80;
      const t = easedProgress;
      const mt = 1 - t;
      nextX = mt*mt*mt*startX + 3*mt*mt*t*control1X + 3*mt*t*t*control2X + t*t*t*waypoint1X;
      nextY = mt*mt*mt*startY + 3*mt*mt*t*control1Y + 3*mt*t*t*control2Y + t*t*t*waypoint1Y;
    } else if (nextProgress < 0.40) {
      const segmentProgress = (nextProgress - 0.20) / 0.20;
      const easedProgress = easeInOutCubic(segmentProgress);
      const control1X = waypoint1X + 60;
      const control1Y = waypoint1Y + (waypoint2Y - waypoint1Y) * 0.3;
      const control2X = waypoint2X + 60;
      const control2Y = waypoint1Y + (waypoint2Y - waypoint1Y) * 0.7;
      const t = easedProgress;
      const mt = 1 - t;
      nextX = mt*mt*mt*waypoint1X + 3*mt*mt*t*control1X + 3*mt*t*t*control2X + t*t*t*waypoint2X;
      nextY = mt*mt*mt*waypoint1Y + 3*mt*mt*t*control1Y + 3*mt*t*t*control2Y + t*t*t*waypoint2Y;
    } else if (nextProgress < 0.60) {
      const segmentProgress = (nextProgress - 0.40) / 0.20;
      const easedProgress = easeInOutCubic(segmentProgress);
      const control1X = waypoint2X - (waypoint2X - waypoint3X) * 0.25;
      const control1Y = waypoint2Y + 80;
      const control2X = waypoint2X - (waypoint2X - waypoint3X) * 0.75;
      const control2Y = waypoint3Y + 80;
      const t = easedProgress;
      const mt = 1 - t;
      nextX = mt*mt*mt*waypoint2X + 3*mt*mt*t*control1X + 3*mt*t*t*control2X + t*t*t*waypoint3X;
      nextY = mt*mt*mt*waypoint2Y + 3*mt*mt*t*control1Y + 3*mt*t*t*control2Y + t*t*t*waypoint3Y;
    } else if (nextProgress < 0.80) {
      const segmentProgress = (nextProgress - 0.60) / 0.20;
      const easedProgress = easeInOutCubic(segmentProgress);
      const control1X = waypoint3X - 60;
      const control1Y = waypoint3Y - (waypoint3Y - waypoint4Y) * 0.3;
      const control2X = waypoint4X - 60;
      const control2Y = waypoint3Y - (waypoint3Y - waypoint4Y) * 0.7;
      const t = easedProgress;
      const mt = 1 - t;
      nextX = mt*mt*mt*waypoint3X + 3*mt*mt*t*control1X + 3*mt*t*t*control2X + t*t*t*waypoint4X;
      nextY = mt*mt*mt*waypoint3Y + 3*mt*mt*t*control1Y + 3*mt*t*t*control2Y + t*t*t*waypoint4Y;
    } else {
      const segmentProgress = (nextProgress - 0.80) / 0.20;
      const easedProgress = easeInOutCubic(segmentProgress);
      const control1X = waypoint4X + (finalX - waypoint4X) * 0.25;
      const control1Y = waypoint4Y - 100;
      const control2X = waypoint4X + (finalX - waypoint4X) * 0.75;
      const control2Y = finalY - 50;
      const t = easedProgress;
      const mt = 1 - t;
      nextX = mt*mt*mt*waypoint4X + 3*mt*mt*t*control1X + 3*mt*t*t*control2X + t*t*t*finalX;
      nextY = mt*mt*mt*waypoint4Y + 3*mt*mt*t*control1Y + 3*mt*t*t*control2Y + t*t*t*finalY;
    }

    // Calculate angle from velocity (direction of movement) - look AHEAD
    const dx = nextX - currentX;
    const dy = nextY - currentY;
    const targetAngle = Math.atan2(dy, dx) + Math.PI / 2;
    // Much slower interpolation for very smooth, momentum-based turns
    const shipAngle = lerpAngle(shipAngleRef.current, targetAngle, 0.04);
    shipAngleRef.current = shipAngle;

    // Clear canvas with transparent background
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw spaceship
    const spaceshipImg = spriteImages['spaceship-top-8bit.png'];
    const flameImg = spriteImages['flame-top-8bit.png'];

    if (spaceshipImg) {
      const surveyScale = 0.85 * zoom;
      const fromScale = spaceshipScaleRef.current > 0 ? spaceshipScaleRef.current : surveyScale;
      const lift = Math.min(1, rawProgress / 0.18);
      const shipScale = fromScale + (surveyScale - fromScale) * easeOutCubic(lift);
      drawSpaceship(
        ctx,
        currentX,
        currentY,
        shipScale,
        shipAngle,
        1.0,
        timestamp,
        spaceshipImg,
        flameImg
      );
    }

    // Update progress
    setAnimationProgress(rawProgress);

    // Continue animation if not complete
    if (rawProgress < 1) {
      animationFrameRef.current = requestAnimationFrame(animateMapFlight);
    } else {
      // Map flight complete, move to landing phase
      dispatch(setAnimationPhase('map_landing'));
      startTimeRef.current = null;
      // Store final position for landing - ENSURE it's exactly above center
      spaceshipXRef.current = finalX;
      spaceshipYRef.current = finalY;
      spaceshipScaleRef.current = 0.85 * zoom;
      dispatch(setShowLandingPad(true)); // Show landing pad
    }
  }, [spritesLoaded, spriteImages, easeInOutCubic, drawSpaceship, lerpAngle, dispatch]);

  // Animation loop for map landing (Issue #54: Spaceship landing on base tile - Smooth deceleration)
  const animateMapLanding = useCallback((timestamp: number) => {
    if (!canvasRef.current || !spritesLoaded || !game?.map?.plots) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Initialize start time
    if (startTimeRef.current === null) {
      startTimeRef.current = timestamp;
    }

    const elapsed = timestamp - startTimeRef.current;
    const duration = 3000; // 3 seconds for slower, more dramatic landing
    const rawProgress = Math.min(elapsed / duration, 1);

    // CRITICAL: Get actual HexGrid canvas position to align with landing pad
    // The HexGrid is an 800x600 canvas that may be positioned/scaled in the layout
    const hexGridCanvas = document.querySelector('.hex-grid-canvas') as HTMLCanvasElement;
    
    let landingPadX: number;
    let landingPadY: number;
    
    if (hexGridCanvas) {
      // Get the HexGrid canvas position relative to viewport
      const hexGridRect = hexGridCanvas.getBoundingClientRect();
      const animationRect = canvas.getBoundingClientRect();
      
      // Calculate center of HexGrid in animation canvas coordinates
      // HexGrid's center pixel is at (hexGridCanvas.width/2, hexGridCanvas.height/2)
      // which corresponds to hex (0,0) - the landing pad
      const hexGridCenterX = hexGridRect.left + hexGridRect.width / 2 - animationRect.left;
      const hexGridCenterY = hexGridRect.top + hexGridRect.height / 2 - animationRect.top;
      
      // Convert from screen coordinates to canvas coordinates
      // (account for canvas being scaled to fit its container)
      const scaleX = canvas.width / animationRect.width;
      const scaleY = canvas.height / animationRect.height;
      
      landingPadX = hexGridCenterX * scaleX;
      landingPadY = hexGridCenterY * scaleY;
      
      console.log('🎯 Landing pad position:', { landingPadX, landingPadY, hexGridRect, animationRect, scaleX, scaleY });
    } else {
      // Fallback to calculation if HexGrid canvas not found
      console.warn('⚠️ HexGrid canvas not found, using fallback coordinates');
      const gameBoardCenterX = canvas.width / 2;
      const gameBoardCenterY = canvas.height / 2;
      const baseHex = { q: 0, r: 0 };
      const basePixel = hexToPixel(baseHex, gameBoardCenterX, gameBoardCenterY);
      landingPadX = basePixel.x;
      landingPadY = basePixel.y;
    }

    const zoom = surveyBoardZoom(canvas.width, canvas.height);

    // Start from above base tile (from map flight end position)
    // Use saved position, but if not set, default to directly above landing pad
    const startX = spaceshipXRef.current !== undefined ? spaceshipXRef.current : landingPadX;
    const startY = spaceshipYRef.current !== undefined ? spaceshipYRef.current : landingPadY - 100 * zoom;

    // Landing sequence with smooth deceleration - ALWAYS LANDS ON CENTER
    // Phase 1 (0-0.25): Quick align to center X position
    // Phase 2 (0.25-0.5): Hover and stabilize directly over landing pad
    // Phase 3 (0.5-1.0): Slow descent with deceleration, getting smaller (going down and back)
    let currentX: number, currentY: number, shipScale: number;

    if (rawProgress < 0.25) {
      // Phase 1: Quick align to exact center X - this ensures we're centered
      const phaseProgress = rawProgress / 0.25;
      const easedProgress = easeInOutCubic(phaseProgress);

      // Move to EXACT center X position above landing pad
      const hoverHeight = 80 * zoom; // Start higher
      currentX = startX + (landingPadX - startX) * easedProgress;
      currentY = startY + (landingPadY - hoverHeight - startY) * easedProgress;
      shipScale = 0.85 * zoom;

    } else if (rawProgress < 0.5) {
      // Phase 2: Stable hover EXACTLY over landing pad center - minor bobbing
      const phaseProgress = (rawProgress - 0.25) / 0.25;
      const hoverHeight = 80 * zoom;

      currentX = landingPadX; // EXACT center
      // Gentle bobbing motion during hover
      const bobAmount = Math.sin(phaseProgress * Math.PI * 3) * 2 * zoom;
      currentY = landingPadY - hoverHeight + bobAmount;
      shipScale = 0.85 * zoom;

    } else {
      // Phase 3: Vertical descent with deceleration - going DOWN and getting SMALLER
      const phaseProgress = (rawProgress - 0.5) / 0.5;
      // Use easeOutCubic for strong deceleration at end
      const easedProgress = easeOutCubic(phaseProgress);

      const hoverHeight = 80 * zoom;
      const startDescent = landingPadY - hoverHeight;
      const endDescent = landingPadY; // Land exactly on center

      currentX = landingPadX; // Stay EXACTLY centered throughout descent
      currentY = startDescent + (endDescent - startDescent) * easedProgress;

      // Gradually shrink as it descends (simulating distance)
      shipScale = (0.85 - (easedProgress * 0.55)) * zoom; // Shrink from 0.85 to 0.30
    }

    // Smooth rotation to landing orientation with flames pointing DOWN
    // For top-down view landing, flames should point toward bottom of screen
    const targetAngle = 0; // Flames point down (toward bottom of screen)
    const shipAngle = lerpAngle(shipAngleRef.current, targetAngle, 0.10);
    shipAngleRef.current = shipAngle;

    // Keep at full opacity - transformation will handle the fade
    const opacity = 1.0;

    // Clear canvas with transparent background
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw spaceship - ALWAYS at exact landing pad position in final phase
    const spaceshipImg = spriteImages['spaceship-top-8bit.png'];
    const flameImg = spriteImages['flame-top-8bit.png'];

    if (spaceshipImg) {
      drawSpaceship(
        ctx,
        currentX,
        currentY,
        shipScale,
        shipAngle,
        opacity,
        timestamp,
        spaceshipImg,
        flameImg
      );
    }

    // Update progress
    setAnimationProgress(rawProgress);

    // Continue animation if not complete
    if (rawProgress < 1) {
      animationFrameRef.current = requestAnimationFrame(animateMapLanding);
    } else {
      // Landing complete, move to transformation phase
      dispatch(setAnimationPhase('map_transformation'));
      startTimeRef.current = null;
      dispatch(setShowLandingPad(false)); // Hide landing pad
    }
  }, [spritesLoaded, spriteImages, game?.map?.plots, easeInOutCubic, easeOutCubic, drawSpaceship, lerpAngle, dispatch]);

  // Animation loop for map transformation (Issue #55: Spaceship to base transformation)
  const animateMapTransformation = useCallback((timestamp: number) => {
    if (!canvasRef.current || !spritesLoaded || !game?.map?.plots) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (startTimeRef.current === null) {
      startTimeRef.current = timestamp;
    }

    const elapsed = timestamp - startTimeRef.current;
    const duration = 2000; // 2 seconds for transformation
    const rawProgress = Math.min(elapsed / duration, 1);
    const progress = easeInOutCubic(rawProgress);

    // CRITICAL: Use the SAME landing pad coordinates from landing phase
    // Do NOT recalculate - reuse the exact position where the rocket landed
    const hexGridCanvas = document.querySelector('.hex-grid-canvas') as HTMLCanvasElement;
    
    let transformX: number;
    let transformY: number;
    
    if (hexGridCanvas) {
      // Get the HexGrid canvas position (same calculation as landing)
      const hexGridRect = hexGridCanvas.getBoundingClientRect();
      const animationRect = canvas.getBoundingClientRect();
      
      const hexGridCenterX = hexGridRect.left + hexGridRect.width / 2 - animationRect.left;
      const hexGridCenterY = hexGridRect.top + hexGridRect.height / 2 - animationRect.top;
      
      const scaleX = canvas.width / animationRect.width;
      const scaleY = canvas.height / animationRect.height;
      
      transformX = hexGridCenterX * scaleX;
      transformY = hexGridCenterY * scaleY;
      
      console.log('🔄 Transformation at position:', { transformX, transformY });
    } else {
      // Fallback - use saved landing position
      console.warn('⚠️ Using fallback transformation position');
      const gameBoardCenterX = canvas.width / 2;
      const gameBoardCenterY = canvas.height / 2;
      const baseHex = { q: 0, r: 0 };
      const basePixel = hexToPixel(baseHex, gameBoardCenterX, gameBoardCenterY);
      transformX = basePixel.x;
      transformY = basePixel.y;
    }

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const spaceshipImg = spriteImages['spaceship-top-8bit.png'];
    const baseImg = spriteImages['base-8bit.png'];

    if (spaceshipImg && baseImg) {
      // NO transforms - draw directly in the same coordinate system as landing
      // This ensures the transformation happens exactly where the rocket landed
      
      // Phase 1 (0-0.4): Small spaceship fades out
      if (progress < 0.4) {
        const phaseProgress = progress / 0.4;
        const easedProgress = 1 - easeInOutCubic(phaseProgress); // Reverse easing for fade out
        ctx.globalAlpha = easedProgress;
        
        const zoom = surveyBoardZoom(canvas.width, canvas.height);
        const spaceshipSize = 19 * zoom; // 0.30 * 64, in the fitted board's pixels
        ctx.drawImage(
          spaceshipImg, 
          transformX - (spaceshipSize / 2), 
          transformY - (spaceshipSize / 2), 
          spaceshipSize, 
          spaceshipSize
        );
        ctx.globalAlpha = 1.0;

      } else {
        // Phase 2 (0.4-1.0): Base grows from small to full tile size
        const phaseProgress = (progress - 0.4) / 0.6;
        const easedProgress = easeInOutCubic(phaseProgress);
        
        // Separate opacity and size for smoother finish
        // Opacity: fade in during first 70% of phase
        const opacityProgress = Math.min(phaseProgress / 0.7, 1.0);
        ctx.globalAlpha = opacityProgress;
        
        const zoom = surveyBoardZoom(canvas.width, canvas.height);
        const startSize = 19 * zoom; // Match final spaceship size
        const endSize = 65 * zoom; // Match HexGrid base icon at the fitted zoom
        const currentSize = startSize + (endSize - startSize) * easedProgress;
        
        ctx.drawImage(
          baseImg, 
          transformX - (currentSize / 2), 
          transformY - (currentSize / 2), 
          currentSize, 
          currentSize
        );
        ctx.globalAlpha = 1.0;
      }
    }

    setAnimationProgress(rawProgress);

    if (rawProgress < 1) {
      animationFrameRef.current = requestAnimationFrame(animateMapTransformation);
    } else {
      // Animation complete - report to server and finish
      // CRITICAL: Must call reportPhaseComplete before dispatching 'complete' phase
      // Use setTimeout to ensure the call happens after current render cycle
      if (!phaseCompleteCallledRef.current && game?.gameId && user?.userId) {
        phaseCompleteCallledRef.current = true;
        console.log('🎮 Completing phase "start_game", calling phase_complete...');
        
        // Call phase_complete API - it will update game state immediately
        // The phase change will be detected by the useEffect that watches game.phase
        reportPhaseComplete(game.gameId, user.userId, game.etag);
        
        // Mark animation as complete locally (UI state)
        // The actual phase transition will happen when game state updates
        setTimeout(() => {
          dispatch(setAnimationPhase('complete'));
          setOverlayOpacity(0);
          dispatch(setInteractionEnabled(true)); // Re-enable interaction
        }, 50);
      } else {
        // Fallback if already called (shouldn't happen)
        dispatch(setAnimationPhase('complete'));
        setOverlayOpacity(0);
        dispatch(setInteractionEnabled(true));
      }
      
      startTimeRef.current = null;
    }
  }, [spritesLoaded, spriteImages, game?.map?.plots, game?.gameId, game?.etag, user?.userId, easeInOutCubic, reportPhaseComplete, dispatch]);

  // Start animation when phase is start_game and sprites are loaded
  useEffect(() => {
    if (game?.phase === 'start_game') {
      dispatch(setInteractionEnabled(false)); // Disable interaction
      // DO NOT reset pan/zoom here, as it causes a visual jump.
      // Instead, we will apply the transform to the canvas context.
      
      // Reset phase complete flag when animation starts
      phaseCompleteCallledRef.current = false;
      
      if (animationPhase === 'idle') {
        revealBoardRef.current = false;
        setRevealBoard(false);
        dispatch(setAnimationPhase('moon_approach'));
      }

      // CRITICAL: Only start animation if not already running
      // This prevents the animation from restarting on every render
      if (animationFrameRef.current) {
        console.log('⏭️  Animation already in progress, skipping restart');
        return; // Animation already running
      }

      if (animationPhase === 'moon_approach' && spritesLoaded) {
        startTimeRef.current = null;
        animationFrameRef.current = requestAnimationFrame(animateMoonApproach);
      } else if (animationPhase === 'camera_lock' && spritesLoaded) {
        startTimeRef.current = null;
        animationFrameRef.current = requestAnimationFrame(animateCameraLock);
      } else if (animationPhase === 'orbital_flight' && spritesLoaded) {
        startTimeRef.current = null;
        animationFrameRef.current = requestAnimationFrame(animateOrbitalFlight);
      } else if (animationPhase === 'map_cut') {
        // Map cut doesn't need sprites, just the game map
        startTimeRef.current = null;
        animationFrameRef.current = requestAnimationFrame(animateMapCut);
      } else if (animationPhase === 'map_entry' && spritesLoaded) {
        // Map entry needs sprites
        startTimeRef.current = null;
        animationFrameRef.current = requestAnimationFrame(animateMapEntry);
      } else if (animationPhase === 'map_flight' && spritesLoaded) {
        // Map flight needs sprites - continues from map_entry position
        startTimeRef.current = null;
        animationFrameRef.current = requestAnimationFrame(animateMapFlight);
      } else if (animationPhase === 'map_landing' && spritesLoaded) {
        // Map landing needs sprites
        startTimeRef.current = null;
        animationFrameRef.current = requestAnimationFrame(animateMapLanding);
      } else if (animationPhase === 'map_transformation' && spritesLoaded) {
        startTimeRef.current = null;
        animationFrameRef.current = requestAnimationFrame(animateMapTransformation);
      }
      
      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = undefined;
        }
        // Ensure interaction is re-enabled if component unmounts mid-animation
        dispatch(setInteractionEnabled(true));
      };
    }
    // CRITICAL: Only depend on phase state changes, not animation functions
    // Animation functions are stable via useCallback and don't need to trigger re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.phase, spritesLoaded, animationPhase, dispatch]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const overlay = canvas?.parentElement;
    if (!canvas || !overlay) return;
    const apply = () => {
      const rect = overlay.getBoundingClientRect();
      if (rect.width < 2 || rect.height < 2) return;
      const next = surveyCanvasSize(rect.width, rect.height, window.devicePixelRatio || 1);
      if (canvas.width !== next.width) canvas.width = next.width;
      if (canvas.height !== next.height) canvas.height = next.height;
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(overlay);
    return () => observer.disconnect();
  }, [animationPhase]);

  // Don't render if not in start_game phase
  if (game?.phase !== 'start_game' || animationPhase === 'complete') {
    return null;
  }
  // if (game?.phase === 'start_game') {
  //   console.log('🎬 StartGameAnimation render:', {
  //     phase: game.phase,
  //     animationPhase,
  //     spritesLoaded,
  //     hasLandmark: !!game.landmark,
  //     hasMap: !!game.map
  //   });
  // }

  return (
    <div 
      className="start-game-animation-overlay"
      style={{ opacity: overlayOpacity }}
      data-phase={animationPhase}
      data-reveal={revealBoard ? 'board' : undefined}
    >
      <canvas
        ref={canvasRef}
        className="start-game-animation-canvas"
      />
      <div className="start-game-animation-info">
        <p>
          {animationPhase === 'moon_approach' && 'Approaching Moon...'}
          {animationPhase === 'camera_lock' && 'Landing Approach...'}
          {animationPhase === 'orbital_flight' && 'Entering Orbit...'}
          {animationPhase === 'map_cut' && 'Establishing Base...'}
          {animationPhase === 'map_entry' && 'Entering Map...'}
          {animationPhase === 'map_flight' && 'Surveying Area...'}
          {animationPhase === 'map_landing' && 'Landing...'}
          {animationPhase === 'map_transformation' && 'Establishing Base...'}
        </p>
        <div className="animation-progress-bar">
          <div 
            className="animation-progress-fill"
            style={{ 
              width: `${(() => {
                // Total duration: 3s + 12s + 0.8s + 1s + 8s + 3s + 2s = 29.8s
                if (animationPhase === 'moon_approach') {
                  return animationProgress * 10.1; // 3s / 29.8s = ~10.1%
                } else if (animationPhase === 'camera_lock') {
                  return 10.1 + (animationProgress * 46.4); // meets the survey bar
                } else if (animationPhase === 'map_cut') {
                  return 50.4 + (animationProgress * 2.7); // 0.8s / 29.8s = ~2.7% (50.4-53.1%)
                } else if (animationPhase === 'map_entry') {
                  return 53.1 + (animationProgress * 3.4); // 1s / 29.8s = ~3.4% (53.1-56.5%)
                } else if (animationPhase === 'map_flight') {
                  return 56.5 + (animationProgress * 26.8); // 8s / 29.8s = ~26.8% (56.5-83.3%)
                } else if (animationPhase === 'map_landing') {
                  return 83.3 + (animationProgress * 10.1); // 3s / 29.8s = ~10.1% (83.3-93.4%)
                } else if (animationPhase === 'map_transformation') {
                  return 93.4 + (animationProgress * 6.7); // 2s / 29.8s = ~6.7% (93.4-100.1%)
                } else {
                  return 100;
                }
              })()}%` 
            }}
          />
        </div>
      </div>
    </div>
  );
}
